import { clsx } from "clsx";
import { X } from "lucide-react";
import { type ReactNode, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "../../hooks/useFocusTrap";

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function Drawer({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  className,
}: DrawerProps) {
  const trapRef = useFocusTrap(isOpen);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      ref={trapRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex justify-end"
    >
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-black/40 animate-backdrop-in"
        onClick={onClose}
        tabIndex={-1}
        aria-label="Zamknij"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          "relative flex h-full w-full max-w-xl flex-col bg-panel shadow-2xl animate-slide-in-right",
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-edge px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold text-main">{title}</h2>
            {subtitle && <p className="mt-0.5 text-sm text-dim">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 -mt-1 rounded-lg p-1.5 text-faint transition-colors hover:bg-muted hover:text-sub"
            aria-label="Zamknij"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable content */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ scrollbarGutter: "stable" }}
        >
          {children}
        </div>

        {/* Sticky footer */}
        {footer && (
          <div className="border-t border-edge bg-subtle/80 px-6 py-4 backdrop-blur-sm">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
